/**
 * Documents editor — Contents (Page) + Configuration (completion gates only)
 */

import * as React from 'react';
import { Input, Label, Checkbox, cn } from '@ai-content-hub/content-kit';
import { type EditTabDescriptor, CompositeEditShell, CompositeEditContentTab, useLocale } from '@ai-content-hub/content-core';
import type { PageElementData } from '@ai-content-hub/content-elements';
import { PageEdit } from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { DocumentsData, DocumentsConfig } from './schema';
import { defaultDocumentsData, normalizeDocumentsCompletion } from './schema';
import { FileText, Settings } from 'lucide-react';

const ALLOWED_BLOCKS: BlockContentType[] = [
    'rich-text',
    'text',
    'image',
    'audio',
    'video',
    'office-document',
];

export interface DocumentsEditProps {
    value: DocumentsData;
    onChange: (value: DocumentsData) => void;
    config?: DocumentsConfig;
    onConfigChange?: (config: DocumentsConfig) => void;
    disabled?: boolean;
    /** Unused — type does not surface generic ContentEditor panels */
    configSlot?: React.ReactNode;
    className?: string;
    errors?: Record<string, string>;
}

export const DocumentsEdit: React.FC<DocumentsEditProps> = ({
    value: data,
    onChange,
    config,
    onConfigChange,
    disabled = false,
    className,
    errors,
}) => {
    const [activeTab, setActiveTab] = React.useState<'content' | 'config'>('content');
    const { t } = useLocale({ config: config?.i18n });

    const safeData: DocumentsData = {
        ...defaultDocumentsData,
        ...data,
        pageContent: data?.pageContent ?? defaultDocumentsData.pageContent,
    };

    const safeConfig = config ?? {};
    const docRules = normalizeDocumentsCompletion(safeConfig.documents);

    const updateData = (updates: Partial<DocumentsData>) => {
        onChange({ ...safeData, ...updates });
    };

    const handlePageChange = (pageContent: PageElementData) => {
        updateData({ pageContent });
    };

    /** Keep page model in sync so PagePlayer / exports still see title on `pageContent`. */
    const syncPageHeader = (title: string, description: string) => {
        updateData({
            title,
            description,
            pageContent: {
                ...safeData.pageContent,
                title,
                description,
            },
        });
    };

    const sectionCount = safeData.pageContent.sections?.length ?? 0;

    const effectiveTitle =
        (safeData.title ?? '').trim() !== ''
            ? (safeData.title ?? '')
            : (safeData.pageContent.title ?? '');
    const effectiveDescription =
        (safeData.description ?? '').trim() !== ''
            ? (safeData.description ?? '')
            : (safeData.pageContent.description ?? '');

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText, badge: sectionCount },
        { id: 'config', label: t('configTab'), icon: Settings },
    ];

    const updateDocConfig = (
        patch: Partial<{ requireFullScroll: boolean; minimumViewSeconds: number }>,
    ) => {
        onConfigChange?.({
            ...safeConfig,
            documents: {
                ...docRules,
                ...safeConfig.documents,
                ...patch,
            },
        });
    };

    const configPanel = (
        <div className="documents-edit-config-panel space-y-6">
            <div className="flex items-start gap-3">
                <Checkbox
                    id="documents-req-scroll"
                    checked={docRules.requireFullScroll}
                    disabled={disabled}
                    onCheckedChange={(v) => updateDocConfig({ requireFullScroll: v === true })}
                    className="documents-edit-require-scroll mt-1"
                />
                <Label
                    htmlFor="documents-req-scroll"
                    className="documents-edit-require-scroll-label text-sm leading-relaxed cursor-pointer"
                >
                    {t('requireFullScrollLabel')}
                </Label>
            </div>
            <div className="space-y-2">
                <Label htmlFor="documents-min-sec">{t('minViewSecondsLabel')}</Label>
                <Input
                    id="documents-min-sec"
                    type="number"
                    min={0}
                    step={1}
                    value={docRules.minimumViewSeconds}
                    onChange={(e) => {
                        const n = Number(e.target.value);
                        updateDocConfig({
                            minimumViewSeconds: Number.isFinite(n)
                                ? Math.max(0, Math.floor(n))
                                : 0,
                        });
                    }}
                    disabled={disabled}
                    className="documents-edit-min-seconds w-36"
                />
                <p className="documents-edit-config-help text-xs text-muted-foreground">
                    {t('minViewSecondsHint')}
                </p>
            </div>
        </div>
    );

    return (
        <CompositeEditShell
            prefix="documents-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'config')}
            configSlot={configPanel}
            className={cn('documents-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="documents-edit"
                            disabled={disabled}
                            title={{
                                id: 'documents-title',
                                label: t('titleLabel', 'Title'),
                                value: effectiveTitle,
                                onChange: (v) => syncPageHeader(v, effectiveDescription),
                                placeholder: t('titlePlaceholder', 'Documents title'),
                                required: true,
                            }}
                            titleInputClassName="documents-edit-title"
                            description={{
                                id: 'documents-desc',
                                label: t('descLabel', 'Instructions (optional)'),
                                value: effectiveDescription,
                                onChange: (v) => syncPageHeader(effectiveTitle, v),
                                placeholder: t('descPlaceholder', 'Short instructions'),
                            }}
                            descriptionAsInput
                            descriptionInputClassName="documents-edit-description"
                            page={
                                <>
                                    <PageEdit
                                        value={safeData.pageContent}
                                        onChange={handlePageChange}
                                        disabled={disabled}
                                        hideSettings
                                        allowedBlockTypes={ALLOWED_BLOCKS}
                                        className="documents-edit-page"
                                    />
                                    {errors?.pageContent ? (
                                        <p className="documents-edit-error text-sm text-destructive mt-2">
                                            {errors.pageContent}
                                        </p>
                                    ) : null}
                                </>
                            }
                        />
                    );
                }
                return null;
            }}
        />
    );
};
